import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Check, X, FileText, Gear, ArrowClockwise, DownloadSimple, PencilSimple, Trash, CurrencyEur, Warning } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Situations de travaux (Finances de l'architecte) — A5 :
 * - UN seul bloc de chiffres (P4#2) : marchés, certifié (avancement), payé, à payer,
 *   reste à payer — les mêmes que Paiements (client) et Point MOA (AMO) ;
 * - cycle Déposée → Certifiée → Payée (P4#1, P4#5) : « Certifier », « Marquer payé le … »,
 *   le « J'ai payé » du client affiché pour confirmation ;
 * - une situation certifiée est figée (P4#4) : ni rejet ni suppression ;
 * - acompte prévu au marché, 0 par défaut (P4#6).
 */
const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', className: 'bg-[#FFF8E1] text-[#B8860B] border-[#B8860B]' },
  deposee: { label: 'Déposée — à vérifier', className: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]' },
  validee: { label: 'Certifiée', className: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' },
  complete: { label: 'Certifiée', className: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' },
  payee: { label: 'Payée', className: 'bg-[#2D6A4F] text-white border-[#2D6A4F]' },
  rejetee: { label: 'Rejetée', className: 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]' },
};
const OUVERTES = ['brouillon', 'deposee', 'rejetee'];

const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};
const moisFr = (m) => {
  const x = /^(\d{4})-(\d{2})/.exec(m || '');
  if (!x) return m || '';
  return new Date(Number(x[1]), Number(x[2]) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const aujourdhui = () => new Date().toISOString().slice(0, 10);

function badgeDe(s) {
  if (s.payee) return { label: s.statut_libelle || 'Payée', className: STATUT_CONFIG.payee.className };
  return STATUT_CONFIG[s.statut] || { label: s.statut_libelle || s.statut, className: '' };
}

export default function SituationsTravaux({ chantierId, roleProjet }) {
  const { user } = useAuth();
  // Préparer : rôles MOE ; certifier / noter un paiement : architecte et chef de projet.
  const roleGlobalArchi = user?.role === 'admin' || user?.role === 'architect';
  const canEdit = roleProjet ? ['archi', 'moe_principal', 'moe_membre'].includes(roleProjet) : roleGlobalArchi;
  const canCertifier = roleProjet ? ['archi', 'moe_principal'].includes(roleProjet) : roleGlobalArchi;

  const [synthese, setSynthese] = useState(null);
  const [situations, setSituations] = useState([]);
  const [marches, setMarches] = useState([]);
  const [config, setConfig] = useState(null);
  const [coherence, setCoherence] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [editing, setEditing] = useState(null); // situation en cours d'édition
  const [paiement, setPaiement] = useState(null); // situation à marquer payée

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [syn, sits, lots, conf, coh] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/finances/synthese`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/situations-v2`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/finances/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/finances/coherence-planning`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setSynthese(syn.data);
      setSituations(sits.data || []);
      setMarches(lots.data || []);
      setConfig(conf.data);
      setCoherence(coh.data);
    } catch (e) {
      toast.error('Erreur de chargement des situations');
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  const handleCertifier = async (id) => {
    if (!window.confirm('Certifier cette situation ? Une situation certifiée ne se modifie plus : une correction se fera sur la situation suivante.')) return;
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${id}/valider`, {}, { withCredentials: true });
      toast.success('Situation certifiée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la certification');
    }
  };

  const handleRejeter = async (s) => {
    const motif = window.prompt(s.statut === 'deposee'
      ? "Motif du rejet (l'entreprise le lira pour corriger sa situation) :"
      : 'Motif du rejet ?');
    if (motif === null) return;
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${s.id}/rejeter`, { motif }, { withCredentials: true });
      toast.success('Situation rejetée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette situation (brouillon) ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${id}`, { withCredentials: true });
      toast.success('Supprimée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleAnnulerPaiement = async (id) => {
    if (!window.confirm('Annuler le paiement noté ? La situation redevient « à payer ».')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${id}/paiement`, { withCredentials: true });
      toast.success('Paiement annulé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSyncPlanning = async (id) => {
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${id}/sync-planning`, {}, { withCredentials: true });
      toast.success('Avancement repris du planning');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
    }
  };

  const handleExportSynthese = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/finances/synthese/export-xlsx`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `synthese_finances_${chantierId}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur export');
    }
  };

  const handleExportPdf = async (id, num) => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${id}/export-pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `situation_n${num}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur export PDF');
    }
  };

  if (loading) return <div className="p-8 text-center text-[#6B6B6B]" data-testid="situations-loading">Chargement...</div>;

  const t = synthese?.totaux || {};
  const pctFin = Math.min(100, Math.max(0, t.pct_avancement_financier || 0));
  const aVerifier = situations.filter((s) => s.statut === 'deposee');
  const signales = situations.filter((s) => s.certifiee && !s.payee && s.paiement_signale_le);

  return (
    <div data-testid="situations-travaux">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h3 className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Situations de travaux</h3>
          <p className="text-xs text-[#6B6B6B] mt-1">
            Demandes de paiement des entreprises : déposées, vérifiées et certifiées par l'architecte, puis payées par le client.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-none gap-2" onClick={handleExportSynthese} data-testid="export-synthese-btn">
            <DownloadSimple size={16} weight="bold" /> Export Excel
          </Button>
          {canCertifier && (
            <Button variant="outline" className="rounded-none gap-2" onClick={() => setShowConfig(true)} data-testid="open-config-btn">
              <Gear size={16} weight="bold" /> Paramètres
            </Button>
          )}
          {canEdit && (
            <Button className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-2" onClick={() => setShowCreate(true)} data-testid="create-situation-btn">
              <Plus size={16} weight="bold" /> Nouvelle situation
            </Button>
          )}
        </div>
      </div>

      {/* À traiter : situations déposées par les entreprises, paiements signalés par le client */}
      {canCertifier && (aVerifier.length > 0 || signales.length > 0) && (
        <div className="border border-[#1E3A5F] bg-[#F3F7FB] p-3 mb-4 text-sm space-y-1" data-testid="situations-a-traiter">
          {aVerifier.map((s) => (
            <p key={s.id} className={s.verification_en_retard ? 'text-[#8B3A3A] font-medium' : 'text-[#1E3A5F]'}>
              Situation n° {s.numero_situation} de {s.entreprise_nom || "l'entreprise"} ({s.lot_code}) déposée le {fmtDate(s.deposee_le)} :
              {' '}à vérifier et certifier avant le <strong>{fmtDate(s.verifier_avant)}</strong>
              {' '}({synthese?.delai_verification_jours || 15} jours){s.verification_en_retard ? ' — délai dépassé' : ''}.
            </p>
          ))}
          {signales.map((s) => (
            <p key={s.id} className="text-[#2D6A4F]">
              Le client indique avoir payé la situation n° {s.numero_situation} de {s.entreprise_nom} le {fmtDate(s.paiement_signale_le)} :
              {' '}confirmez avec « Marquer payé ».
            </p>
          ))}
        </div>
      )}

      {/* Alerte cohérence Planning / Finances */}
      {coherence && coherence.nb_alertes > 0 && (
        <div className="bg-[#FFF5EB] border border-[#C4773B] p-3 mb-4 flex items-start gap-2" data-testid="coherence-alert">
          <Warning size={18} className="text-[#C4773B] flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-[#6B4A25]">
              {coherence.nb_alertes} lot{coherence.nb_alertes > 1 ? 's ont' : ' a'} un écart planning / situations {'≥'} 20 points
            </p>
            <div className="mt-1 text-xs text-[#6B4A25] flex flex-wrap gap-x-3 gap-y-1">
              {(coherence.lignes || []).filter(r => r.alerte).map((r) => (
                <span key={r.lot_code}>
                  <strong>{r.lot_code}</strong> : planning {r.pct_planning?.toFixed(0)} % · certifié {r.pct_finances_valide?.toFixed(0)} %
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* A5 / P4#2 — LE bloc de chiffres (mêmes définitions que Paiements et Point MOA) */}
      <div className="bg-white border border-[#E0E0DB] p-5 mb-5" data-testid="finances-chiffres">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mb-4">
          <Stat label="Marchés HT" value={t.montant_marches_ht} aide="avenants acceptés compris" />
          <Stat label="Certifié HT" value={t.cumul_travaux_ht} accent aide="travaux certifiés" />
          <Stat label="Payé HT" value={t.total_paye_ht} color="#2D6A4F" aide="paiements notés" />
          <Stat label="À payer HT" value={t.total_a_payer_ht} color="#C75B39" aide="certifié, pas encore payé" />
          <Stat label="Reste à payer HT" value={t.reste_a_payer_ht} color="#8B3A3A" aide="marchés − payé" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#6B6B6B] uppercase tracking-wider">Avancement (travaux certifiés / marchés)</span>
            <span className="text-sm font-bold text-[#1E3A5F]">{pctFin.toFixed(1).replace('.', ',')} %</span>
          </div>
          <div className="w-full h-2 bg-[#F5F5F3] border border-[#E0E0DB]">
            <div className="h-full bg-[#C4773B]" style={{ width: `${pctFin}%` }} />
          </div>
          {t.retenues_garantie_en_cours > 0 && (
            <p className="text-xs text-[#6B6B6B] mt-2">Retenue de garantie conservée : {fmt(t.retenues_garantie_en_cours)} € HT (comprise dans le reste à payer).</p>
          )}
        </div>
      </div>

      {/* Détail par lot : mêmes colonnes */}
      <div className="bg-white border border-[#E0E0DB] mb-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="synthese-table">
          <thead className="bg-[#F5F5F3]">
            <tr className="text-left text-xs uppercase tracking-wider text-[#6B6B6B]">
              <th className="p-3">Lot</th>
              <th className="p-3">Entreprise</th>
              <th className="p-3 text-right">Marché HT</th>
              <th className="p-3 text-right">Certifié</th>
              <th className="p-3 text-center">Avancement</th>
              <th className="p-3 text-right">Payé</th>
              <th className="p-3 text-right">À payer</th>
              <th className="p-3 text-right">Reste à payer</th>
            </tr>
          </thead>
          <tbody>
            {(synthese?.lignes || []).length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-[#6B6B6B] italic">Aucun marché à afficher</td></tr>
            ) : (synthese.lignes).map((row, idx) => (
              <tr key={idx} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF8]">
                <td className="p-3 font-medium text-[#2C2C2C]">{row.lot_code}<div className="text-xs text-[#6B6B6B]">{row.lot_nom}</div></td>
                <td className="p-3 text-[#2C2C2C]">
                  {row.entreprise_nom || <span className="italic text-[#6B6B6B]">—</span>}
                  {row.nb_a_verifier > 0 && <span className="block text-[11px] text-[#1E3A5F]">{row.nb_a_verifier} situation à vérifier</span>}
                </td>
                <td className="p-3 text-right tabular-nums">{fmt(row.montant_marche_ht)} €</td>
                <td className="p-3 text-right tabular-nums text-[#C4773B] font-medium">{fmt(row.cumul_travaux_ht)} €</td>
                <td className="p-3 text-center tabular-nums">{(row.pct_avancement || 0).toFixed(1).replace('.', ',')} %</td>
                <td className="p-3 text-right tabular-nums text-[#2D6A4F]">{fmt(row.paye_cumul_ht)} €</td>
                <td className="p-3 text-right tabular-nums text-[#C75B39]">{fmt(row.a_payer_ht)} €</td>
                <td className="p-3 text-right tabular-nums">{fmt(row.reste_a_payer_ht)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Liste situations */}
      <div>
        <h4 className="text-base text-[#2C2C2C] mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>Historique des situations ({situations.length})</h4>
        {situations.length === 0 ? (
          <div className="bg-white border border-[#E0E0DB] p-8 text-center text-[#6B6B6B] italic">
            Aucune situation pour ce chantier. Les entreprises déposent la leur depuis leur espace (« Mes situations ») ; vous pouvez aussi en préparer une.
          </div>
        ) : (
          <div className="space-y-2">
            {situations.map((s) => {
              const badge = badgeDe(s);
              const ouverte = OUVERTES.includes(s.statut);
              return (
              <div key={s.id} className="bg-white border border-[#E0E0DB] p-4" data-testid={`situation-row-${s.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                      <span className="text-[#2C2C2C] font-medium">Situation n° {s.numero_situation}</span>
                      <span className="text-xs text-[#6B6B6B]">{moisFr(s.mois)}</span>
                      <Badge className={`rounded-none border ${badge.className}`} data-testid={`situation-statut-${s.id}`}>{badge.label}</Badge>
                      {s.auto_synced_from_planning && (
                        <span
                          title={s.auto_synced_at ? `Dernière reprise : ${new Date(s.auto_synced_at).toLocaleString('fr-FR')} (avancement moyen ${s.auto_synced_avg} %)` : ''}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-[#1E3A5F] bg-[#E3F2FD] text-[#1E3A5F]"
                          data-testid={`auto-synced-badge-${s.id}`}
                        >
                          <ArrowClockwise size={10} /> Repris du planning {s.auto_synced_avg != null ? `· ${Math.round(s.auto_synced_avg)} %` : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#6B6B6B]">
                      {s.lot_code} — {s.lot_nom} · {s.entreprise_nom || '—'}
                    </div>
                    <div className="text-xs text-[#6B6B6B] mt-1 space-x-2">
                      {s.deposee_le && <span>Déposée par l'entreprise le {fmtDate(s.deposee_le)}</span>}
                      {s.statut === 'deposee' && s.verifier_avant && (
                        <span className={s.verification_en_retard ? 'text-[#8B3A3A] font-medium' : ''}>· à certifier avant le {fmtDate(s.verifier_avant)}</span>
                      )}
                      {s.certifiee && <span>Certifiée le {fmtDate(s.validated_at)}{s.validated_by_name ? ` par ${s.validated_by_name}` : ''}</span>}
                      {s.certifiee && !s.payee && s.echeance_paiement && <span>· échéance {fmtDate(s.echeance_paiement)}</span>}
                      {s.paiement_signale_le && !s.payee && (
                        <span className="text-[#2D6A4F]">· le client indique avoir payé le {fmtDate(s.paiement_signale_le)}</span>
                      )}
                    </div>
                    {s.commentaire_entreprise && <div className="mt-1 text-xs text-[#4A4A4A] italic">« {s.commentaire_entreprise} »</div>}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-sm">
                      <MiniStat label="Cumul HT" value={s.montant_travaux_cumul_ht} />
                      <MiniStat label="Ce mois HT" value={s.montant_travaux_ce_mois_ht} accent />
                      <MiniStat label="Retenue" value={s.retenue_garantie_montant} />
                      <MiniStat label="Net HT" value={s.montant_net_ht} />
                      <MiniStat label="Net TTC" value={s.montant_net_ttc} bold />
                    </div>
                    {s.alerte && <div className="mt-2 text-xs text-[#8B3A3A] bg-[#FFEBEE] border border-[#8B3A3A] p-2">{s.alerte}</div>}
                    {s.motif_rejet && <div className="mt-2 text-xs text-[#8B3A3A] italic">Motif du rejet : {s.motif_rejet}</div>}
                  </div>
                  <div className="flex flex-row flex-wrap md:flex-col gap-1 md:items-end">
                    {canEdit && ouverte && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => setEditing(s)} data-testid={`edit-situation-${s.id}`}>
                          <PencilSimple size={12} /> {s.statut === 'deposee' ? 'Vérifier' : 'Éditer'}
                        </Button>
                        {s.statut === 'brouillon' && (
                          <Button size="sm" variant="outline" className="rounded-none gap-1 h-8 text-[#1E3A5F]" onClick={() => handleSyncPlanning(s.id)} data-testid={`sync-planning-${s.id}`} title="Reprendre l'avancement moyen du planning pour ce lot">
                            <ArrowClockwise size={12} /> Avancement du planning
                          </Button>
                        )}
                      </>
                    )}
                    {canCertifier && ouverte && (
                      <>
                        <Button size="sm" className="bg-[#2D6A4F] hover:bg-[#1E4A35] rounded-none gap-1 h-8 text-white" onClick={() => handleCertifier(s.id)} data-testid={`validate-situation-${s.id}`}>
                          <Check size={12} /> Certifier
                        </Button>
                        {s.statut !== 'rejetee' && (
                          <Button size="sm" variant="outline" className="rounded-none gap-1 h-8 text-[#8B3A3A]" onClick={() => handleRejeter(s)} data-testid={`reject-situation-${s.id}`}>
                            <X size={12} /> Rejeter
                          </Button>
                        )}
                      </>
                    )}
                    {canEdit && (s.statut === 'brouillon' || s.statut === 'rejetee') && (
                      <Button size="sm" variant="outline" className="rounded-none gap-1 h-8 text-[#8B3A3A]" onClick={() => handleDelete(s.id)}>
                        <Trash size={12} /> Supprimer
                      </Button>
                    )}
                    {/* A5 / P4#1 — certifiée ≠ payée */}
                    {canCertifier && s.certifiee && !s.payee && (
                      <Button size="sm" className="bg-[#1E3A5F] hover:bg-[#15293F] rounded-none gap-1 h-8 text-white" onClick={() => setPaiement(s)} data-testid={`marquer-payee-${s.id}`}>
                        <CurrencyEur size={12} /> Marquer payé le…
                      </Button>
                    )}
                    {canCertifier && s.payee && (
                      <button type="button" className="text-[11px] text-[#6B6B6B] underline" onClick={() => handleAnnulerPaiement(s.id)} data-testid={`annuler-paiement-${s.id}`}>
                        Annuler le paiement noté
                      </button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => handleExportPdf(s.id, s.numero_situation)} data-testid={`export-pdf-${s.id}`}>
                      <FileText size={12} /> PDF
                    </Button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateSituationDialog
          chantierId={chantierId}
          marches={marches}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {showConfig && config && (
        <ConfigDialog
          chantierId={chantierId}
          config={config}
          marches={marches}
          acomptesParCode={Object.fromEntries((synthese?.lignes || []).map((l) => [l.lot_code, l.acompte_ht || 0]))}
          canEdit={canCertifier}
          onClose={() => setShowConfig(false)}
          onSaved={(c) => { setConfig(c); setShowConfig(false); load(); }}
        />
      )}
      {editing && (
        <EditSituationDialog
          chantierId={chantierId}
          situation={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {paiement && (
        <PaiementDialog
          chantierId={chantierId}
          situation={paiement}
          onClose={() => setPaiement(null)}
          onSaved={() => { setPaiement(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color, accent, aide }) {
  return (
    <div>
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">{label}</p>
      <p className="text-xl font-bold tabular-nums" style={{ color: color || (accent ? '#C4773B' : '#2C2C2C'), fontFamily: 'DM Serif Display, serif' }}>
        {fmt(value)} €
      </p>
      {aide && <p className="text-[11px] text-[#9A9A9A]">{aide}</p>}
    </div>
  );
}

function MiniStat({ label, value, accent, bold }) {
  return (
    <div>
      <div className="text-[10px] text-[#6B6B6B] uppercase">{label}</div>
      <div className={`tabular-nums ${bold ? 'font-bold text-[#1E3A5F]' : ''} ${accent ? 'text-[#C4773B]' : 'text-[#2C2C2C]'}`}>{fmt(value)} €</div>
    </div>
  );
}

// ==================== « MARQUER PAYÉ LE … » ====================

function PaiementDialog({ chantierId, situation, onClose, onSaved }) {
  const [date, setDate] = useState((situation.paiement_signale_le || '').slice(0, 10) || aujourdhui());
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${situation.id}/paiement`, { payee_le: date }, { withCredentials: true });
      toast.success('Paiement noté');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="paiement-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Marquer payée — situation n° {situation.numero_situation}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#4A4A4A]">
            {situation.entreprise_nom} · {situation.lot_code} — <strong>{fmt(situation.montant_net_ttc)} € TTC</strong>.
            {' '}« Déjà payé » ne compte que les situations dont le paiement est noté.
          </p>
          {situation.paiement_signale_le && (
            <p className="text-xs text-[#2D6A4F]">Le client indique avoir payé le {fmtDate(situation.paiement_signale_le)}.</p>
          )}
          <div>
            <Label>Payée le</Label>
            <Input type="date" value={date} max={aujourdhui()} onChange={(e) => setDate(e.target.value)} className="rounded-none mt-1" required data-testid="paiement-date" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#1E3A5F] hover:bg-[#15293F] rounded-none text-white" data-testid="paiement-valider">
              {saving ? 'Enregistrement…' : 'Marquer payé'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CREATE DIALOG ====================

function CreateSituationDialog({ chantierId, marches, onClose, onCreated }) {
  const [lotCode, setLotCode] = useState('');
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!lotCode) return toast.error('Lot requis');
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2`, { lot_code: lotCode, mois }, { withCredentials: true });
      toast.success('Situation créée');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="create-situation-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Nouvelle situation de travaux</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Lot / Marché</Label>
            <Select value={lotCode} onValueChange={setLotCode}>
              <SelectTrigger className="rounded-none mt-1" data-testid="select-lot"><SelectValue placeholder="Sélectionner un lot" /></SelectTrigger>
              <SelectContent>
                {marches.map((m) => (
                  <SelectItem key={m.id || m.code} value={m.code}>{m.code} — {m.nom} {m.entreprise_nom ? `(${m.entreprise_nom})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mois de situation</Label>
            <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="rounded-none mt-1" data-testid="input-mois" />
          </div>
          <div className="text-xs text-[#6B6B6B] bg-[#FFF5EB] border border-[#F0CDA6] p-3">
            Les lignes reprennent les postes du lot dans la DPGF (retrouvé par son numéro), sinon une ligne au forfait. L'avancement déjà certifié est repris. Les entreprises peuvent aussi déposer leur situation elles-mêmes.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none" data-testid="submit-create-situation">
              {saving ? 'Création...' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CONFIG DIALOG ====================

function ConfigDialog({ chantierId, config: initial, marches = [], acomptesParCode = {}, canEdit, onClose, onSaved }) {
  const [cfg, setCfg] = useState(initial);
  const [saving, setSaving] = useState(false);
  // A5 / P4#6 — l'acompte se déclare sur le marché (0 par défaut), plus en pourcentage global
  const initiaux = Object.fromEntries(marches.map((m) => [m.id, acomptesParCode[m.code] ?? 0]));
  const [acomptes, setAcomptes] = useState(initiaux);

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { acompte_par_defaut_pct: _ignore, ...reste } = cfg;
      const res = await axios.put(`${API_URL}/api/chantiers/${chantierId}/finances/config`, reste, { withCredentials: true });
      await Promise.all(marches
        .filter((m) => Number(acomptes[m.id] || 0) !== Number(initiaux[m.id] || 0))
        .map((m) => axios.put(`${API_URL}/api/chantiers/${chantierId}/marches/${m.id}/acompte`,
          { acompte_ht: Number(acomptes[m.id] || 0) }, { withCredentials: true })));
      toast.success('Configuration enregistrée');
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const update = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="config-finances-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Paramètres financiers du chantier</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <ConfigRow label="Retenue de garantie" active={cfg.retenue_garantie_active} pct={cfg.retenue_garantie_pct}
            onToggle={(v) => update('retenue_garantie_active', v)} onPct={(v) => update('retenue_garantie_pct', v)} disabled={!canEdit} testid="rg" />
          <ConfigRow label="Compte prorata" active={cfg.prorata_active} pct={cfg.prorata_pct}
            onToggle={(v) => update('prorata_active', v)} onPct={(v) => update('prorata_pct', v)} disabled={!canEdit} testid="prorata" />
          <ConfigRow label="Retenue bonne fin" active={cfg.retenue_bonne_fin_active} pct={cfg.retenue_bonne_fin_pct}
            onToggle={(v) => update('retenue_bonne_fin_active', v)} onPct={(v) => update('retenue_bonne_fin_pct', v)} disabled={!canEdit} testid="bf" />
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#E0E0DB]">
            <div>
              <Label className="text-xs">TVA par défaut (%)</Label>
              <Input type="number" step="0.1" value={cfg.tva_par_defaut} onChange={(e) => update('tva_par_defaut', parseFloat(e.target.value) || 0)} className="rounded-none mt-1" disabled={!canEdit} />
            </div>
            <div>
              <Label className="text-xs">Récupération de l'acompte à partir de (% d'avancement)</Label>
              <Input type="number" step="0.1" value={cfg.seuil_recuperation_acompte_pct} onChange={(e) => update('seuil_recuperation_acompte_pct', parseFloat(e.target.value) || 0)} className="rounded-none mt-1" disabled={!canEdit} />
            </div>
            <div>
              <Label className="text-xs">Pénalités retard / jour (€)</Label>
              <Input type="number" step="0.01" value={cfg.penalites_retard_par_jour} onChange={(e) => update('penalites_retard_par_jour', parseFloat(e.target.value) || 0)} className="rounded-none mt-1" disabled={!canEdit} />
            </div>
          </div>
          {marches.length > 0 && (
            <div className="pt-2 border-t border-[#E0E0DB]" data-testid="acomptes-marches">
              <p className="text-xs font-semibold text-[#2C2C2C] mb-1">Acompte prévu au marché (HT)</p>
              <p className="text-[11px] text-[#6B6B6B] mb-2">0 si aucun acompte n'a été versé à l'entreprise : rien n'est alors retenu sur ses situations.</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {marches.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{m.code} — {m.nom}{m.entreprise_nom ? ` (${m.entreprise_nom})` : ''}</span>
                    <Input type="number" step="0.01" min="0" value={acomptes[m.id] ?? 0} disabled={!canEdit}
                      onChange={(e) => setAcomptes({ ...acomptes, [m.id]: e.target.value })}
                      className="rounded-none w-32 h-8 text-right" data-testid={`acompte-${m.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Fermer</Button>
            {canEdit && (
              <Button type="submit" disabled={saving} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" data-testid="save-config-btn">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfigRow({ label, active, pct, onToggle, onPct, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex items-center gap-3 flex-1">
        <Switch checked={active} onCheckedChange={onToggle} disabled={disabled} data-testid={`switch-${testid}`} />
        <Label className="text-sm text-[#2C2C2C]">{label}</Label>
      </div>
      <div className="flex items-center gap-1">
        <Input type="number" step="0.1" value={pct} onChange={(e) => onPct(parseFloat(e.target.value) || 0)}
          className="rounded-none w-20 h-8 text-right" disabled={disabled || !active} data-testid={`input-${testid}`} />
        <span className="text-sm text-[#6B6B6B]">%</span>
      </div>
    </div>
  );
}

// ==================== EDIT DIALOG ====================

function EditSituationDialog({ chantierId, situation, onClose, onSaved }) {
  const [lignes, setLignes] = useState(situation.lignes || []);
  const [rgPct, setRgPct] = useState(situation.retenue_garantie_pct || 0);
  const [prPct, setPrPct] = useState(situation.prorata_pct || 0);
  const [bfPct, setBfPct] = useState(situation.retenue_bonne_fin_pct || 0);
  const [penalites, setPenalites] = useState(situation.penalites_retard || 0);
  const [primes, setPrimes] = useState(situation.primes_avance || 0);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(situation);

  const updateLigne = (idx, patch) => {
    const next = [...lignes];
    next[idx] = { ...next[idx], ...patch };
    setLignes(next);
  };

  const addLigne = () => setLignes([...lignes, {
    poste_id: `manual-${Date.now()}`, designation: '', unite: 'u', quantite_marche: 0, prix_unitaire: 0,
    quantite_realisee_cumul_precedent: 0, quantite_realisee_ce_mois: 0, quantite_realisee_cumul: 0,
    montant_ce_mois: 0, montant_cumul: 0, pct_avancement: 0,
  }]);

  const removeLigne = (idx) => setLignes(lignes.filter((_, i) => i !== idx));

  const handleSync = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${situation.id}/sync-dpgf`, {}, { withCredentials: true });
      setLignes(res.data.lignes || []);
      setPreview(res.data);
      toast.success('Postes repris de la DPGF');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sync');
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API_URL}/api/chantiers/${chantierId}/situations-v2/${situation.id}`, {
        lignes, retenue_garantie_pct: rgPct, prorata_pct: prPct, retenue_bonne_fin_pct: bfPct,
        penalites_retard: penalites, primes_avance: primes,
      }, { withCredentials: true });
      setPreview(res.data);
      setLignes(res.data.lignes || []);
      toast.success('Enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => {
    await save();
    onSaved();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="edit-situation-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Situation n° {situation.numero_situation} — {situation.lot_code} · {situation.entreprise_nom}
          </DialogTitle>
        </DialogHeader>
        {situation.statut === 'deposee' && (
          <p className="text-xs text-[#1E3A5F] bg-[#F3F7FB] border border-[#1E3A5F] p-2 mb-2">
            Déposée par l'entreprise le {fmtDate(situation.deposee_le)}. Vérifiez les quantités, ajustez si besoin, puis certifiez
            {situation.verifier_avant ? ` avant le ${fmtDate(situation.verifier_avant)}` : ''}.
          </p>
        )}

        {/* Toolbar */}
        <div className="flex gap-2 mb-3 flex-wrap">
          <Button variant="outline" className="rounded-none gap-1 h-8" onClick={handleSync} data-testid="sync-dpgf-btn">
            <ArrowClockwise size={14} /> Reprendre les postes de la DPGF
          </Button>
          <Button variant="outline" className="rounded-none gap-1 h-8" onClick={addLigne}>
            <Plus size={14} /> Ajouter une ligne
          </Button>
        </div>

        {/* Lignes */}
        <div className="border border-[#E0E0DB] overflow-x-auto mb-4">
          <table className="w-full text-xs" data-testid="lignes-table">
            <thead className="bg-[#F5F5F3]">
              <tr className="text-left uppercase text-[#6B6B6B]">
                <th className="p-2">Désignation</th>
                <th className="p-2">Unité</th>
                <th className="p-2 text-right">Qté marché</th>
                <th className="p-2 text-right">PU HT</th>
                <th className="p-2 text-right">Cumul préc.</th>
                <th className="p-2 text-right">Qté ce mois</th>
                <th className="p-2 text-right">Cumul</th>
                <th className="p-2 text-right">Montant mois</th>
                <th className="p-2 text-right">Cumul HT</th>
                <th className="p-2 text-center">% avct</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {lignes.length === 0 ? (
                <tr><td colSpan={11} className="p-4 text-center italic text-[#6B6B6B]">Aucune ligne. Reprenez les postes de la DPGF ou ajoutez des lignes.</td></tr>
              ) : lignes.map((l, idx) => {
                const cumulPrec = parseFloat(l.quantite_realisee_cumul_precedent) || 0;
                const mois = parseFloat(l.quantite_realisee_ce_mois) || 0;
                const pu = parseFloat(l.prix_unitaire) || 0;
                const qm = parseFloat(l.quantite_marche) || 0;
                const cumul = cumulPrec + mois;
                const mMois = mois * pu;
                const mCumul = cumul * pu;
                const pct = qm ? (cumul / qm * 100) : 0;
                return (
                  <tr key={idx} className="border-t border-[#F0F0EB]">
                    <td className="p-1"><Input value={l.designation} onChange={(e) => updateLigne(idx, { designation: e.target.value })} className="rounded-none h-8 text-xs" /></td>
                    <td className="p-1"><Input value={l.unite} onChange={(e) => updateLigne(idx, { unite: e.target.value })} className="rounded-none h-8 w-16 text-xs" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={l.quantite_marche} onChange={(e) => updateLigne(idx, { quantite_marche: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-right text-xs" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={l.prix_unitaire} onChange={(e) => updateLigne(idx, { prix_unitaire: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-right text-xs" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={cumulPrec} onChange={(e) => updateLigne(idx, { quantite_realisee_cumul_precedent: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-right text-xs" /></td>
                    <td className="p-1 bg-[#FFF5EB]"><Input type="number" step="0.01" value={mois} onChange={(e) => updateLigne(idx, { quantite_realisee_ce_mois: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-right text-xs font-medium" data-testid={`ligne-mois-${idx}`} /></td>
                    <td className="p-2 text-right tabular-nums">{cumul.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(mMois)}</td>
                    <td className="p-2 text-right tabular-nums font-medium">{fmt(mCumul)}</td>
                    <td className="p-2 text-center tabular-nums">{pct.toFixed(1)}%</td>
                    <td className="p-1 text-center"><button type="button" onClick={() => removeLigne(idx)} className="text-[#8B3A3A] hover:bg-[#FFEBEE] p-1"><Trash size={12} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paramètres situation */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div><Label className="text-xs">Retenue garantie %</Label><Input type="number" step="0.1" value={rgPct} onChange={(e) => setRgPct(parseFloat(e.target.value) || 0)} className="rounded-none mt-1 h-8" data-testid="edit-rg-pct" /></div>
          <div><Label className="text-xs">Prorata %</Label><Input type="number" step="0.1" value={prPct} onChange={(e) => setPrPct(parseFloat(e.target.value) || 0)} className="rounded-none mt-1 h-8" /></div>
          <div><Label className="text-xs">Retenue bonne fin %</Label><Input type="number" step="0.1" value={bfPct} onChange={(e) => setBfPct(parseFloat(e.target.value) || 0)} className="rounded-none mt-1 h-8" /></div>
          <div><Label className="text-xs">Pénalités retard €</Label><Input type="number" step="0.01" value={penalites} onChange={(e) => setPenalites(parseFloat(e.target.value) || 0)} className="rounded-none mt-1 h-8" /></div>
          <div><Label className="text-xs">Primes avance €</Label><Input type="number" step="0.01" value={primes} onChange={(e) => setPrimes(parseFloat(e.target.value) || 0)} className="rounded-none mt-1 h-8" /></div>
        </div>

        {/* Récap calculé (après save) */}
        <div className="bg-[#F5F5F3] border border-[#E0E0DB] p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <MiniStat label="Travaux cumul" value={preview.montant_travaux_cumul_ht} />
            <MiniStat label="Ce mois" value={preview.montant_travaux_ce_mois_ht} accent />
            <MiniStat label="Retenue garantie" value={preview.retenue_garantie_montant} />
            <MiniStat label="Prorata" value={preview.prorata_montant} />
            <MiniStat label="Acompte récupéré" value={preview.acompte_a_recuperer_ce_mois} />
            <MiniStat label="Net HT" value={preview.montant_net_ht} />
            <MiniStat label="TVA" value={preview.montant_tva} />
            <MiniStat label="Net TTC" value={preview.montant_net_ttc} bold />
          </div>
          {preview.alerte && <div className="mt-3 text-xs text-[#8B3A3A] bg-[#FFEBEE] border border-[#8B3A3A] p-2">{preview.alerte}</div>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Fermer</Button>
          <Button onClick={save} disabled={saving} variant="outline" className="rounded-none" data-testid="recalc-btn">
            {saving ? 'Calcul...' : 'Recalculer'}
          </Button>
          <Button onClick={saveAndClose} disabled={saving} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" data-testid="save-situation-btn">
            {saving ? 'Enregistrement...' : 'Enregistrer & fermer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
